import { formatDate } from '@/lib/format';
import type { Holiday, ParentSmsOutcome } from '@/types/holiday';
import type { HolidayFormValues } from './schemas/holiday.schema';

/**
 * `15 Aug 2026`, or `20 Oct 2026 – 25 Oct 2026` for a range.
 *
 * Both ends are inclusive, so a one-day holiday is shown as the single date it
 * is rather than as a range that starts and finishes on the same morning.
 */
export function formatHolidayRange(holiday: Pick<Holiday, 'startDate' | 'endDate'>): string {
  if (holiday.startDate === holiday.endDate) return formatDate(holiday.startDate);
  return `${formatDate(holiday.startDate)} – ${formatDate(holiday.endDate)}`;
}

/** "1 day", "5 days" — the count the server worked out, spelled for a reader. */
export function describeDays(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

/** Fills the form from a declared holiday, for the edit dialog. */
export function toFormValues(holiday: Holiday): HolidayFormValues {
  return {
    name: holiday.name,
    startDate: holiday.startDate,
    endDate: holiday.endDate,
    type: holiday.type,
    // The API returns null for "no note"; an input cannot hold null.
    description: holiday.description ?? '',
    isStaffWorking: holiday.isStaffWorking,
    // An edit never texts anybody; the box is offered only when declaring.
    notifyParentsBySms: false,
  };
}

/**
 * "Sent to 212 families · 3 failed · 5 have no number" — the confirmation's
 * second line. Only the parts that are not zero, so a clean send reads clean.
 */
export function describeParentSms(outcome: ParentSmsOutcome): string {
  const parts = [`Sent to ${outcome.sent} ${outcome.sent === 1 ? 'family' : 'families'}`];
  if (outcome.failed > 0) parts.push(`${outcome.failed} failed`);
  if (outcome.skipped > 0) {
    parts.push(`${outcome.skipped} ${outcome.skipped === 1 ? 'has' : 'have'} no number on record`);
  }
  return parts.join(' · ');
}

/**
 * Whether a holiday touches a given `YYYY-MM` at all.
 *
 * Overlap, not "starts in" — a vacation running 28 December to 3 January is a
 * January holiday as much as a December one, and an institute looking at January
 * needs to see it. Matches what the server's `month` filter does, so filtering
 * here and filtering there give the same answer.
 *
 * Compared as strings: `YYYY-MM-DD` sorts lexically the same way it sorts
 * chronologically, which is the whole reason the API speaks in it.
 */
export function overlapsMonth(
  holiday: Pick<Holiday, 'startDate' | 'endDate'>,
  month: string,
): boolean {
  if (!month) return true;
  return holiday.startDate.slice(0, 7) <= month && holiday.endDate.slice(0, 7) >= month;
}

/**
 * How many of a holiday's days fall inside one month, both ends counted.
 *
 * A summer break from 15 April to 12 June is 16 days of April, all 31 of May
 * and 12 of June — not 59 in each. Worked in UTC on the date strings, so a
 * device's time zone can never move a day across a month boundary.
 */
export function daysInMonth(
  holiday: Pick<Holiday, 'startDate' | 'endDate'>,
  month: string,
): number {
  const [year, monthNumber] = month.split('-').map(Number);
  const monthStart = `${month}-01`;
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const monthEnd = `${month}-${String(lastDay).padStart(2, '0')}`;

  const from = holiday.startDate > monthStart ? holiday.startDate : monthStart;
  const to = holiday.endDate < monthEnd ? holiday.endDate : monthEnd;
  if (from > to) return 0;

  const DAY_MS = 86_400_000;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

/** The `YYYY-MM` a holiday is filed under — where it begins. */
export function monthOf(holiday: Pick<Holiday, 'startDate'>): string {
  return holiday.startDate.slice(0, 7);
}

/**
 * The holiday declared on one date, or null on an ordinary day.
 *
 * Holidays are stored as ranges — a fortnight's Dussehra break is one row — so
 * anything asking "is the institute open on the 14th" has to look inside the
 * range rather than at its first day. Lives here rather than in whichever
 * screen needed it first: the timetable grid, the mini calendar and the
 * holidays page are all asking the same question (CLAUDE.md §3).
 *
 * First declaration wins, matching the server's own rule for two holidays
 * overlapping the same day.
 *
 * Compared as strings: `YYYY-MM-DD` sorts lexically the way it sorts
 * chronologically, which is the whole reason the API speaks in it.
 */
export function holidayOn(holidays: Holiday[] | null, date: string): Holiday | null {
  if (!holidays) return null;
  return holidays.find((holiday) => holiday.startDate <= date && holiday.endDate >= date) ?? null;
}

/** The line a date picker shows under its calendar for the days `holidayDayNote` marks. */
export const HOLIDAY_DAY_NOTE_LEGEND = 'Holiday (from the Academic Calendar)';

/**
 * A `DatePicker` `dayNote` that marks each holiday with its name, so whoever is
 * choosing a date can see the days the school is closed. Shared by every
 * picker that needs it, so they all mark the same days in the same way.
 */
export function holidayDayNote(holidays: Holiday[] | null): (day: string) => string | null {
  return (day) => holidayOn(holidays, day)?.name ?? null;
}
